import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthContext';
import Input from '@shared/Input';
import Button from '@shared/Button';
import { toast } from 'react-hot-toast';
import { CheckCircle, XCircle } from 'lucide-react';

const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { resetPassword, loading, error } = useAuth();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [resetStatus, setResetStatus] = useState<'success' | 'failure' | null>(null); // 'success' or 'failure'

  useEffect(() => {
    // Optional: You might want to verify the token validity on component mount
    // However, the backend will handle this during the actual reset attempt.
    if (!token) {
      toast.error('No reset token provided.');
      navigate('/login');
    }
  }, [token, navigate]);

  const validateForm = (): boolean => {
    let isValid = true;
    setPasswordError('');
    setConfirmPasswordError('');

    if (!newPassword) {
      setPasswordError('New password is required.');
      isValid = false;
    } else if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      isValid = false;
    }

    if (!confirmPassword) {
      setConfirmPasswordError('Confirm password is required.');
      isValid = false;
    } else if (newPassword !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match.');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm() && token) {
      try {
        const response = await resetPassword(token, newPassword);
        if (response.success) {
          setResetStatus('success');
          toast.success('Password reset successfully! You can now log in.');
          setTimeout(() => navigate('/login'), 3000);
        } else {
          setResetStatus('failure');
          toast.error(response.message || 'Failed to reset password.');
        }
      } catch (err: any) {
        setResetStatus('failure');
        toast.error(err.message || 'An unexpected error occurred.');
      }
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8 space-y-6">
        <h2 className="text-3xl font-bold text-center text-gray-900">Reset Password</h2>
        <p className="text-center text-gray-600">
          Enter your new password below.
        </p>

        {resetStatus === 'success' && (
          <div className="bg-green-50 border-l-4 border-green-400 text-green-800 p-4 mb-4" role="alert">
            <p className="flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              Your password has been successfully reset. Redirecting to login...
            </p>
          </div>
        )}

        {resetStatus === 'failure' && error && (
          <div className="bg-red-50 border-l-4 border-red-400 text-red-800 p-4 mb-4" role="alert">
            <p className="flex items-center">
              <XCircle className="w-5 h-5 mr-2" />
              {error}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <Input
              id="newPassword"
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              error={!!passwordError}
              className="w-full"
              disabled={loading || resetStatus === 'success'}
            />
            {passwordError && <p className="text-error text-xs mt-1">{passwordError}</p>}
          </div>
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={!!confirmPasswordError}
              className="w-full"
              disabled={loading || resetStatus === 'success'}
            />
            {confirmPasswordError && <p className="text-error text-xs mt-1">{confirmPasswordError}</p>}
          </div>

          <Button
            type="submit"
            className="w-full"
            loading={loading}
            disabled={loading || resetStatus === 'success'}
            variant="primary"
            size="lg"
          >
            Reset Password
          </Button>
        </form>
        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm font-medium text-primary-600 hover:text-primary-500">
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
